import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Modal,
  Switch,
  Select,
} from "antd";

import { useReactToPrint } from "react-to-print";

import Swal from "sweetalert2";

import logo from "./zulu.png";

import "./RoyaltyPurchaseReceipt.css";

const { Option } = Select;

const RoyaltyPurchaseReceipt = ({
  open,
  onClose,

  receiptData = [],

  receiptTotal = 0,

  formatNumber = (number) =>
    Number(number || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

  receiptCustomer,

  balanceBefore = 0,

  balanceAfter = 0,
}) => {
  // =========================================================
  // PRINT REF
  // =========================================================

  const printRef = useRef(null);

  // =========================================================
  // PRINTER MODE
  // =========================================================

  const [printerMode, setPrinterMode] = useState(
    localStorage.getItem("royaltyPrinterMode") ||
      "thermal"
  );

  // =========================================================
  // DEFAULT PRINTER
  // =========================================================

  const [
    setDefaultPrinter,
    setSetDefaultPrinter,
  ] = useState(
    localStorage.getItem(
      "royaltySetDefaultPrinter"
    ) === "true"
  );

  // =========================================================
  // SAVE PRINTER SETTINGS
  // =========================================================

  useEffect(() => {
    localStorage.setItem(
      "royaltyPrinterMode",
      printerMode
    );

    localStorage.setItem(
      "royaltySetDefaultPrinter",
      String(setDefaultPrinter)
    );
  }, [
    printerMode,
    setDefaultPrinter,
  ]);

  // =========================================================
  // NORMALIZE RECEIPT ITEMS
  // =========================================================

  const normalizedReceiptItems = useMemo(() => {
    if (!Array.isArray(receiptData)) {
      return [];
    }

    return receiptData.map((item, index) => {
      // =====================================================
      // PRODUCT NAME
      // =====================================================

      const productName =
        item?.product_name ||
        item?.product?.name ||
        item?.name ||
        "Product";

      // =====================================================
      // QUANTITY
      // =====================================================

      const quantity = Number(
        item?.quantity_taken ??
          item?.quantity ??
          item?.qty ??
          1
      );

      // =====================================================
      // PRICE
      // =====================================================

      const price = Number(
        item?.price ??
          item?.unit_price ??
          item?.purchase_price_at_sale ??
          item?.price_at_sale ??
          item?.product?.price ??
          0
      );

      // =====================================================
      // BACKEND TOTAL
      // =====================================================

      const backendTotal = Number(
        item?.total ??
          item?.line_total ??
          item?.subtotal ??
          item?.total_amount ??
          item?.revenue ??
          0
      );

      // =====================================================
      // CALCULATED TOTAL
      // =====================================================

      const calculatedTotal =
        price * quantity;

      // =====================================================
      // FINAL TOTAL
      // =====================================================

      const finalItemTotal =
        backendTotal > 0
          ? backendTotal
          : calculatedTotal;

      return {
        ...item,

        _receiptIndex: index,

        product_name: productName,

        quantity_taken:
          Number.isFinite(quantity) &&
          quantity > 0
            ? quantity
            : 1,

        price:
          Number.isFinite(price)
            ? price
            : 0,

        total:
          Number.isFinite(finalItemTotal)
            ? finalItemTotal
            : 0,
      };
    });
  }, [receiptData]);

  // =========================================================
  // CALCULATED RECEIPT TOTAL
  // =========================================================

  const calculatedReceiptTotal = useMemo(() => {
    return normalizedReceiptItems.reduce(
      (total, item) => {
        return (
          total +
          Number(item?.total || 0)
        );
      },
      0
    );
  }, [normalizedReceiptItems]);

  // =========================================================
  // SAFE RECEIPT TOTAL
  // =========================================================

  const safeReceiptTotal = useMemo(() => {
    const suppliedTotal =
      Number(receiptTotal);

    if (
      Number.isFinite(suppliedTotal) &&
      suppliedTotal > 0
    ) {
      return suppliedTotal;
    }

    if (
      calculatedReceiptTotal > 0
    ) {
      return calculatedReceiptTotal;
    }

    return 0;
  }, [
    receiptTotal,
    calculatedReceiptTotal,
  ]);

  // =========================================================
  // FORMAT MONEY
  // =========================================================

  const formatMoney = (value) => {
    const number = Number(value || 0);

    return formatNumber(
      Number.isFinite(number)
        ? number
        : 0
    );
  };

  // =========================================================
  // DEBUG
  // =========================================================

  useEffect(() => {
    if (!open) {
      return;
    }

    console.log(
      "========================================"
    );

    console.log(
      "ROYALTY PURCHASE RECEIPT"
    );

    console.log(
      "RECEIPT DATA:",
      receiptData
    );

    console.log(
      "NORMALIZED ITEMS:",
      normalizedReceiptItems
    );

    console.log(
      "RECEIPT TOTAL:",
      receiptTotal
    );

    console.log(
      "SAFE TOTAL:",
      safeReceiptTotal
    );

    console.log(
      "CUSTOMER:",
      receiptCustomer
    );

    console.log(
      "BALANCE BEFORE:",
      balanceBefore
    );

    console.log(
      "BALANCE AFTER:",
      balanceAfter
    );

    console.log(
      "PRINT REF:",
      printRef.current
    );

    console.log(
      "========================================"
    );
  }, [
    open,
    receiptData,
    normalizedReceiptItems,
    receiptTotal,
    safeReceiptTotal,
    receiptCustomer,
    balanceBefore,
    balanceAfter,
  ]);

  // =========================================================
  // PRINT RECEIPT
  //
  // SAME PATTERN AS YOUR WORKING PurchaseReceipt.js
  // =========================================================

  const handleRoyaltyPrint = useReactToPrint({
    content: () => printRef.current,

    documentTitle:
      `Royalty-Receipt-${Date.now()}`,

    // =======================================================
    // PRINT PAGE STYLE
    // =======================================================

    pageStyle: `
      @page {
        margin: 5mm;
      }

      @media print {

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;
        }

        .royalty-receipt {
          display: block !important;
          visibility: visible !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 15px !important;
          background: #ffffff !important;
          color: #000000 !important;
          box-sizing: border-box !important;
        }

        .royalty-receipt * {
          visibility: visible !important;
          color: #000000 !important;
        }

        .receipt-header {
          display: block !important;
          visibility: visible !important;
        }

        .receipt-logo {
          display: block !important;
          visibility: visible !important;
        }

        .receipt-body {
          display: block !important;
          visibility: visible !important;
        }

        .receipt-item {
          display: flex !important;
          visibility: visible !important;
        }

        .receipt-total {
          display: block !important;
          visibility: visible !important;
        }

        .receipt-balance {
          display: block !important;
          visibility: visible !important;
        }

        .receipt-footer {
          display: block !important;
          visibility: visible !important;
        }

        .printer-controls {
          display: none !important;
          visibility: hidden !important;
        }
      }
    `,

    // =======================================================
    // BEFORE CONTENT
    // =======================================================

    onBeforeGetContent: () => {
      Swal.fire({
        title: "Preparing Receipt...",
        text: "Opening printer dialog...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,

        didOpen: () => {
          Swal.showLoading();
        },
      });

      return Promise.resolve();
    },

    // =======================================================
    // BEFORE PRINT
    // =======================================================

    onBeforePrint: () => {
      Swal.close();
    },

    // =======================================================
    // AFTER PRINT
    // =======================================================

    onAfterPrint: () => {
      Swal.fire({
        icon: "success",
        title: "Print Complete",
        text: "Royalty receipt sent to printer.",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    removeAfterPrint: true,
  });

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      width={900}
      destroyOnClose={false}
    >
      <div className="royalty-container">

        {/* ===================================================
            RECEIPT
        =================================================== */}

        <div
          ref={printRef}
          className={
            `royalty-receipt ${printerMode}`
          }
        >

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="receipt-header">

            <img
              src={logo}
              alt="ZuluWhiskey Logo"
              className="receipt-logo"
            />

            <h1>
              ZULUWHISKEY
            </h1>

            <h3>
              ROYALTY CARD PAYMENT
            </h3>

            <p>
              Tel: +231-773-275-388 |
              +231-889-239-865
            </p>

            <p>
              Johnsonville, Kpelleh Town,
              Opposite God's Willing Gas Station,
              Monrovia, Liberia
            </p>

          </div>

          <hr />

          {/* =================================================
              CUSTOMER
          ================================================= */}

          <div className="receipt-customer">

            <p>
              <b>
                Customer:
              </b>{" "}
              {receiptCustomer?.full_name ||
                receiptCustomer?.name ||
                "N/A"}
            </p>

            <p>
              <b>
                Date:
              </b>{" "}
              {new Date().toLocaleString()}
            </p>

          </div>

          <hr />

          {/* =================================================
              ITEMS
          ================================================= */}

          <div className="receipt-body">

            {normalizedReceiptItems.length > 0 ? (

              normalizedReceiptItems.map(
                (item, index) => (

                  <div
                    key={
                      item?.id ||
                      item?.product ||
                      item?._receiptIndex ||
                      index
                    }
                    className="receipt-item"
                  >

                    <span className="item-name">
                      {item.product_name}
                      {" × "}
                      {item.quantity_taken}
                    </span>

                    <span className="item-total">
                      $
                      {formatMoney(
                        item.total
                      )}
                    </span>

                  </div>

                )
              )

            ) : (

              <div className="empty-items">
                No items found
              </div>

            )}

          </div>

          <hr />

          {/* =================================================
              TOTAL
          ================================================= */}

          <div className="receipt-total">

            TOTAL: $

            {formatMoney(
              safeReceiptTotal
            )}

          </div>

          <hr />

          {/* =================================================
              POINT BALANCE
          ================================================= */}

          <div className="receipt-balance">

            <p>
              <b>
                Point Before:
              </b>{" "}
              {formatMoney(
                balanceBefore
              )}
            </p>

            <p>
              <b>
                Balance Points After:
              </b>{" "}
              {formatMoney(
                balanceAfter
              )}
            </p>

          </div>

          <hr />

          {/* =================================================
              FOOTER
          ================================================= */}

          <div className="receipt-footer">

            <p>
              THANK YOU FOR SHOPPING
              WITH US 🙏
            </p>

            <small>
              ROYALTY CARD TRANSACTION
            </small>

          </div>

        </div>

        {/* ===================================================
            PRINTER CONTROLS
        =================================================== */}

        <div className="printer-controls">

          <h3>
            Printer Settings
          </h3>

          {/* =================================================
              PRINTER TYPE
          ================================================= */}

          <Select
            value={printerMode}
            onChange={setPrinterMode}
            className="printer-select"
          >

            <Option value="thermal">
              80mm Thermal Printer
            </Option>

            <Option value="a4">
              A4 Printer
            </Option>

          </Select>

          {/* =================================================
              DEFAULT PRINTER
          ================================================= */}

          <div className="default-printer">

            <Switch
              checked={setDefaultPrinter}
              onChange={
                setSetDefaultPrinter
              }
            />

            {" "}

            Set as Default Printer

          </div>

          {/* =================================================
              PRINT BUTTON
          ================================================= */}

          <button
            type="button"
            className="print-btn"
            onClick={() => {

              // ---------------------------------------------
              // CHECK RECEIPT
              // ---------------------------------------------

              if (!printRef.current) {

                Swal.fire({
                  icon: "error",
                  title: "Print Error",
                  text:
                    "Receipt content not found.",
                  confirmButtonText: "OK",
                });

                return;
              }

              // ---------------------------------------------
              // CHECK ITEMS
              // ---------------------------------------------

              if (
                normalizedReceiptItems.length ===
                0
              ) {

                Swal.fire({
                  icon: "warning",
                  title: "No Items",
                  text:
                    "There are no purchased items on this receipt.",
                  confirmButtonText: "OK",
                });

                return;
              }

              // ---------------------------------------------
              // CHECK TOTAL
              // ---------------------------------------------

              if (
                safeReceiptTotal <= 0
              ) {

                Swal.fire({
                  icon: "warning",
                  title:
                    "Invalid Receipt Total",
                  text:
                    "The purchase total could not be determined.",
                  confirmButtonText: "OK",
                });

                return;
              }

              // ---------------------------------------------
              // PRINT
              // ---------------------------------------------

              handleRoyaltyPrint();

            }}
          >
            🖨️ Print Royalty Receipt
          </button>

          {/* =================================================
              CLOSE
          ================================================= */}

          <button
            type="button"
            className="close-btn"
            onClick={onClose}
          >
            Close
          </button>

        </div>

      </div>
    </Modal>
  );
};

export default RoyaltyPurchaseReceipt;